import type { ReleaseDocument } from "@packages/commons";
import { findReleaseDocument, normaliseText } from "@packages/commons";

// a regex check for \ / : * ? " < > | characters
export const DISALLOWED_CHARACTERS_REGEX = /[\\/:*?"|]|(<[^>]*>)/;
export const DISALLOWED_CHARACTERS_MESSAGE = '\\ / : * ? " < > |';

export const validateDocName = (name: string): boolean => Boolean(name) && !DISALLOWED_CHARACTERS_REGEX.test(name);

/** Sanitizes a document name by removing html tags and entities, and replacing non-breaking space */
export const sanitizeDocName = (name: string): string => {
  const sanitized = name
    .replace(/<[^>]*>/g, "") // remove html tags
    .replace(/&nbsp;/g, " ") // replace non-breaking space
    .replace(/&[a-z]+;/g, "") // remove html entities, eg &amp; &gt; etc
    .replace(/\r\n|\r|\n/g, "") // replace return and carriage return with empty string
    .trim();
  // normalise to remove special characters and extra whitespace
  return normaliseText(sanitized);
};

/** Trims the extension from a document name, html tags and trims whitespace. Note this name is also normalised */
export const trimDocName = <S extends string | undefined>(name: S): S =>
  (name && sanitizeDocName(name.replace(/\.(docx|xlsx)$/i, "").trim())) as S;

export const resolveReleaseDocument = (
  documents: unknown,
  referenceOrName: string | undefined,
): ReleaseDocument | undefined => {
  if (!referenceOrName) return undefined;

  const normalisedReference = normaliseText(referenceOrName);
  const normalisedTrimmedReference = normaliseText(trimDocName(referenceOrName));

  return findReleaseDocument(
    documents,
    (doc) =>
      doc.reference === referenceOrName ||
      normaliseText(doc.name) === normalisedReference ||
      normaliseText(trimDocName(doc.name)) === normalisedTrimmedReference,
  );
};

export const getReleaseDocumentDisplayName = (documents: unknown, referenceOrName: string): string =>
  trimDocName(resolveReleaseDocument(documents, referenceOrName)?.name ?? referenceOrName);
